import React, { useState } from 'react';
import { Button, Card, CardHeader, CardBody, Avatar, Input, Switch, Chip, Tabs, Tab, RadioGroup, Radio } from '@nextui-org/react';
import { FiCheckCircle, FiCreditCard, FiCalendar, FiFileText, FiUsers, FiShield, FiPlus } from 'react-icons/fi';
import HeaderComponent from '../components/General/HeaderComponent';

function Premium() {
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [selectedPlan, setSelectedPlan] = useState('starter');
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [sendInvoices, setSendInvoices] = useState(false);
  const [warnBeforeEnd, setWarnBeforeEnd] = useState(false);

  // Plan tanımları
  const plans = [
    {
      id: 'starter',
      name: 'HeyPena Starter',
      tagline: 'Başlangıç için plan',
      price: { monthly: 45, yearly: 530 },
      features: [
        '3 kullanıcı',
        '20 proje',
        '20GB depolama',
        'Canlı güncellemeler',
        'Ödemeler',
        '1 eklenti'
      ],
      popular: false
    },
    {
      id: 'pro',
      name: 'HeyPena Pro',
      tagline: 'Profesyoneller için',
      price: { monthly: 89, yearly: 890 },
      features: [
        '10 kullanıcı',
        'Sınırsız proje',
        '100GB depolama',
        'Canlı güncellemeler',
        'Ödemeler',
        '5 eklenti',
        'Gelişmiş analitik'
      ],
      popular: false
    },
    {
      id: 'business',
      name: 'HeyPena Business',
      tagline: 'Ciddi işletmeler için',
      price: { monthly: 159, yearly: 1590 },
      features: [
        '30 kullanıcı',
        'Sınırsız proje',
        '500GB depolama',
        'Canlı güncellemeler',
        'Ödemeler',
        'Sınırsız eklenti',
        'Gelişmiş analitik',
        'Öncelikli destek'
      ],
      popular: false
    },
    {
      id: 'enterprise',
      name: 'HeyPena Enterprise',
      tagline: 'Büyük şirketler için',
      price: { monthly: 299, yearly: 2990 },
      features: [
        'Sınırsız kullanıcı',
        'Sınırsız proje',
        '1TB depolama',
        'Canlı güncellemeler',
        'Ödemeler',
        'Sınırsız eklenti',
        'Gelişmiş analitik',
        '24/7 öncelikli destek',
        'Özel entegrasyonlar'
      ],
      popular: false
    }
  ];

  // Demo takım üyeleri
  const teamMembers = [
    { id: 1, name: 'Erik K.', location: 'New York, NY', role: 'Software Engineer', initials: 'EK', avatar: null },
    { id: 2, name: 'Betty T.', location: 'San Diego, CA', role: 'Product Manager', initials: 'BT', avatar: null },
    { id: 3, name: 'Esteban C.', location: 'Miami, FL', role: 'UI/UX Designer', initials: 'EC', avatar: null }
  ];

  const currentPlan = plans.find(p => p.id === selectedPlan);
  const remainingSeats = currentPlan ? parseInt(currentPlan.features[0].match(/\d+/)?.[0] || '0') - teamMembers.length : 0;

  const formatPrice = (price) => {
    const amount = billingCycle === 'monthly' ? price.monthly : price.yearly;
    const monthlyAmount = price.monthly;
    const yearlyAmount = price.yearly;
    return {
      monthly: `₺${monthlyAmount}`,
      yearly: `₺${yearlyAmount} yıllık faturalandırma`,
      display: billingCycle === 'monthly' ? `₺${monthlyAmount}` : `₺${monthlyAmount}`
    };
  };

  const selectedPlanData = currentPlan ? formatPrice(currentPlan.price) : null;

  return (
    <div className="w-full dark:bg-dark-100 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 py-6">
        <HeaderComponent title="Premium" />
        
        <div className="max-w-7xl mx-auto mt-6">
          <div className="grid lg:grid-cols-3 gap-6">
          {/* Sol Kolon */}
          <div className="lg:col-span-2 space-y-6">
            {/* Plan Seçimi */}
            <Card className="border border-gray-200 dark:border-dark-300 bg-white dark:bg-dark-300 shadow-none">
              <CardHeader className="pb-4">
                <h2 className="text-lg font-semibold text-[#283252] dark:text-darkWhite-300">Plan Seçin</h2>
              </CardHeader>
              <CardBody className="space-y-4">
                {/* Plan Kartları - 2x2 Grid */}
                <div className="grid grid-cols-2 gap-4">
                  {plans.map((plan) => {
                    const price = formatPrice(plan.price);
                    const isSelected = selectedPlan === plan.id;
                    
                    return (
                      <div
                        key={plan.id}
                        className={`border cursor-pointer transition-all p-4 rounded-lg bg-white dark:bg-dark-300 ${
                          isSelected 
                            ? 'border-green-500 bg-green-50/30 dark:bg-green-900/10' 
                            : 'border-gray-200 dark:border-dark-400'
                        }`}
                        onClick={() => setSelectedPlan(plan.id)}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            {/* Üçgen Logo */}
                            <div className={`w-6 h-6 ${isSelected ? 'text-green-500' : 'text-gray-400'}`}>
                              <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2L2 22h20L12 2z" />
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-sm font-semibold text-[#283252] dark:text-darkWhite-300">
                                {plan.name}
                              </h3>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {plan.tagline}
                              </p>
                            </div>
                          </div>
                          {isSelected && (
                            <FiCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                  HeyPena, işletmeniz büyüdükçe ölçeklenebilen inanılmaz özelliklere sahiptir. 
                  Her plan küçük bir işletme mi yoksa daha büyük bir işletme mi olduğunuza göre mükemmel şekilde uyum sağlar.
                </p>
              </CardBody>
            </Card>

            {/* Takım Koltukları - Geçici olarak gizlendi */}
            {false && (
              <Card className="border border-gray-200 dark:border-dark-300 bg-white dark:bg-dark-300 shadow-none">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between w-full">
                    <h2 className="text-lg font-semibold text-[#283252] dark:text-darkWhite-300">Takım Koltukları</h2>
                    {selectedPlan && (
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {remainingSeats > 0 ? remainingSeats : 0} kalan
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardBody>
                  {selectedPlan ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      {teamMembers.map((member) => (
                        <div
                          key={member.id}
                          className="flex flex-col items-center"
                        >
                          <Avatar
                            name={member.initials}
                            className="w-12 h-12 mb-2"
                            classNames={{
                              base: member.initials === 'EK' 
                                ? "bg-purple-500 text-white"
                                : member.initials === 'BT'
                                ? "bg-orange-500 text-white"
                                : "bg-blue-500 text-white",
                              name: "text-white font-semibold text-xs"
                            }}
                          />
                          <p className="text-xs text-gray-600 dark:text-gray-400 text-center max-w-[80px] truncate">
                            {member.name}
                          </p>
                        </div>
                      ))}
                      <button className="w-12 h-12 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
                        <FiPlus className="w-5 h-5 text-gray-400" />
                      </button>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                      <p className="text-sm">Lütfen önce bir plan seçin</p>
                    </div>
                  )}
                </CardBody>
              </Card>
            )}

            {/* Seçenekler */}
            <Card className="border border-gray-200 dark:border-dark-300 bg-white dark:bg-dark-300 shadow-none">
              <CardHeader className="pb-4">
                <h2 className="text-lg font-semibold text-[#283252] dark:text-darkWhite-300">Seçenekler</h2>
              </CardHeader>
              <CardBody className="space-y-4">
                <Switch
                  isSelected={sendInvoices}
                  onValueChange={setSendInvoices}
                  classNames={{
                    label: "text-sm text-gray-700 dark:text-gray-300"
                  }}
                >
                  Yeni faturaları e-postama gönder
                </Switch>
                <Switch
                  isSelected={warnBeforeEnd}
                  onValueChange={setWarnBeforeEnd}
                  classNames={{
                    label: "text-sm text-gray-700 dark:text-gray-300"
                  }}
                >
                  Faturalama dönemi sona ermeden önce beni uyar
                </Switch>
              </CardBody>
            </Card>

            {/* Faturalama Dönemi */}
            <Card className="border border-gray-200 dark:border-dark-300 bg-white dark:bg-dark-300 shadow-none">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between w-full">
                  <h2 className="text-lg font-semibold text-[#283252] dark:text-darkWhite-300">Faturalama Dönemi</h2>
                  <Button
                    variant="light"
                    size="sm"
                    startContent={<FiFileText className="w-4 h-4" />}
                  >
                    Faturalarım
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <RadioGroup
                  value={billingCycle}
                  onValueChange={setBillingCycle}
                  orientation="horizontal"
                  classNames={{
                    wrapper: "gap-6"
                  }}
                >
                  <Radio value="monthly" classNames={{
                    label: "text-sm text-gray-700 dark:text-gray-300"
                  }}>
                    Aylık
                  </Radio>
                  <Radio value="yearly" classNames={{
                    label: "text-sm text-gray-700 dark:text-gray-300"
                  }}>
                    Yıllık
                  </Radio>
                </RadioGroup>
              </CardBody>
            </Card>
          </div>

          {/* Sağ Kolon */}
          <div className="space-y-6">
            {/* Seçili Plan Detayları */}
            {currentPlan && (
              <Card className="border border-gray-200 dark:border-dark-300 bg-white dark:bg-dark-300 shadow-none">
                <CardBody className="p-6">
                  {/* Logo */}
                  <div className="flex flex-col items-center mb-6">
                    <div className="w-16 h-16 text-green-500 mb-3">
                      <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
                        <path d="M12 2L2 22h20L12 2z" />
                      </svg>
                    </div>
                    <div className="text-center">
                      <h2 className="text-lg font-bold text-[#283252] dark:text-darkWhite-300">
                        HEYPENA
                      </h2>
                      <p className="text-xs text-green-500 font-semibold uppercase">
                        {currentPlan.name.toUpperCase().replace('HEYPENA ', '')}
                      </p>
                    </div>
                  </div>

                  {/* Fiyat */}
                  <div className="mb-4 text-center">
                    <div className="text-2xl font-bold text-[#283252] dark:text-darkWhite-300 mb-1">
                      {selectedPlanData?.display} / ay
                    </div>
                    <div className="text-sm text-green-600 dark:text-green-400 font-medium">
                      {billingCycle === 'yearly' 
                        ? `${selectedPlanData?.yearly}` 
                        : `${currentPlan.price.yearly}₺ yıllık faturalandırma`}
                    </div>
                  </div>

                  {/* Açıklama */}
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 text-center">
                    HeyPena inanılmaz özelliklere sahiptir ve her plan işletmenizin küçük mü yoksa daha büyük mü olduğuna göre mükemmel şekilde uyum sağlar. 
                    HeyPena, işletmeniz büyüdükçe sizinle birlikte ölçeklenebilir.
                  </p>

                  {/* Özellikler - 2 Kolon */}
                  <div className="grid grid-cols-2 gap-3 mt-6">
                    {currentPlan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <FiCheckCircle className="w-4 h-4 text-gray-600 dark:text-gray-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Ödeme Bilgileri */}
            <Card className="border border-gray-200 dark:border-dark-300 bg-white dark:bg-dark-300 shadow-none">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    <FiCreditCard className="text-gray-600 dark:text-gray-400" />
                    <h2 className="text-lg font-semibold text-[#283252] dark:text-darkWhite-300">Ödeme bilgileri</h2>
                  </div>
                  <Button variant="light" size="sm" className="text-xs">
                    Rastgele
                  </Button>
                </div>
              </CardHeader>
              <CardBody className="space-y-4">
                {/* Kart Görseli */}
                <div className="relative bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 rounded-lg p-6 h-40 mb-4">
                  <div className="absolute top-4 left-4">
                    <div className="w-10 h-8 bg-gray-500 dark:bg-gray-400 rounded" />
                  </div>
                  <div className="absolute bottom-6 left-4 right-4">
                    <div className="text-white text-sm mb-2 opacity-80">
                      {cardNumber || 'KART NUMARASI'}
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-white text-xs opacity-80">
                        {cardName || 'KART SAHİBİ'}
                      </div>
                      <div className="text-white text-xs opacity-80">
                        {cardExpiry || 'GEÇERLİLİK'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form Alanları */}
                <Input
                  label="Ad"
                  placeholder="Karttaki ad"
                  value={cardName}
                  onChange={(e) => setCardName(e.target.value)}
                  classNames={{
                    input: "border border-gray-200 dark:border-dark-400",
                    inputWrapper: "border border-gray-200 dark:border-dark-400"
                  }}
                />
                <Input
                  label="Kart Numarası"
                  placeholder="Kredi kartı numarası"
                  value={cardNumber}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\s/g, '');
                    value = value.match(/.{1,4}/g)?.join(' ') || value;
                    setCardNumber(value);
                  }}
                  maxLength={19}
                  classNames={{
                    input: "border border-gray-200 dark:border-dark-400",
                    inputWrapper: "border border-gray-200 dark:border-dark-400"
                  }}
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Son Kullanma"
                    placeholder="AA / YY"
                    value={cardExpiry}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '');
                      if (value.length >= 2) {
                        value = value.slice(0, 2) + ' / ' + value.slice(2, 4);
                      }
                      setCardExpiry(value);
                    }}
                    maxLength={7}
                    classNames={{
                      input: "border border-gray-200 dark:border-dark-400",
                      inputWrapper: "border border-gray-200 dark:border-dark-400"
                    }}
                  />
                  <Input
                    label="CVC"
                    placeholder="3 haneli kod"
                    value={cardCvc}
                    onChange={(e) => {
                      setCardCvc(e.target.value.replace(/\D/g, '').slice(0, 3));
                    }}
                    maxLength={3}
                    type="password"
                    classNames={{
                      input: "border border-gray-200 dark:border-dark-400",
                      inputWrapper: "border border-gray-200 dark:border-dark-400"
                    }}
                  />
                </div>
                <Button
                  className="w-full bg-green-600 hover:bg-green-700 text-white mt-2"
                >
                  Ödeme Yöntemini Kaydet
                </Button>
              </CardBody>
            </Card>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}

export default Premium;
